/**
 * Client → Data → Custom Views: the client's blended tables. Create offers
 * Start a blank view or Clone a view (AgencyAnalytics' AI prompt arrives with
 * AgencyAI, roadmap C8). Deleting a view leaves its widgets saying so.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { EmptyFeature } from '../agency/EmptyFeature.tsx';
import type { Catalogue, Client } from '../reports/reports-api.ts';
import { ConfirmDelete } from './ConfirmDelete.tsx';
import {
  customApi,
  CUSTOM_SOURCE,
  VIEW_PREFIX,
  type CustomView,
  type ViewDefinition,
} from './custom-api.ts';
import { BLANK_VIEW, ViewEditor } from './ViewEditor.tsx';

type Editing = { view: CustomView | null; name: string; definition: ViewDefinition };
const DEFAULT_TIMEZONE = 'Africa/Johannesburg';

export function CustomViewsPanel(props: {
  organisationId: string;
  client: Client;
  catalogue: Catalogue;
  onChanged: () => void;
}): ReactNode {
  const { organisationId, client } = props;
  const [views, setViews] = useState<CustomView[] | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [deleting, setDeleting] = useState<CustomView | null>(null);
  const sources = props.catalogue.sources.filter(
    (source) => source.key !== CUSTOM_SOURCE && !source.key.startsWith(VIEW_PREFIX),
  );
  const load = () => customApi.views(organisationId, client.id).then(setViews, () => setViews([]));
  useEffect(() => void load(), [organisationId, client.id]);
  const changed = () => {
    setEditing(null);
    void load();
    props.onChanged();
  };
  const create = () => setChoosing(true);

  return (
    <div className="cx-panel">
      {views?.length === 0 ? (
        <EmptyFeature
          title="Custom Views"
          lead="Blend several data sources into one table: one dimension, the metrics you choose, a totals row."
          points={[
            'Compare platforms side by side: spend, clicks and leads in one table',
            'Rename values so different sources line up',
            'Add computed columns such as cost per lead',
          ]}
          action={{ label: 'Create View', onClick: create }}
        />
      ) : (
        <div className="ag-table-card">
          <div className="cx-head">
            <p className="ag-table-meta">
              {views === null ? 'Loading…' : `Showing ${views.length} Rows`}
            </p>
            <button type="button" className="btn" onClick={create}>
              Create View
            </button>
          </div>
          <table className="ag-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Data Sources</th>
                <th scope="col">Columns</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(views ?? []).map((view) => (
                <tr key={view.id}>
                  <td>
                    <strong>{view.name}</strong>
                  </td>
                  <td>
                    {view.definition.sources
                      .map(
                        (row) =>
                          sources.find((item) => item.key === row.source)?.label ?? row.source,
                      )
                      .join(', ')}
                  </td>
                  <td>{view.definition.columns.length}</td>
                  <td className="cx-actions">
                    <button
                      type="button"
                      className="ex-link"
                      onClick={() =>
                        setEditing({ view, name: view.name, definition: view.definition })
                      }
                    >
                      Edit
                    </button>
                    <button type="button" className="ex-link" onClick={() => setDeleting(view)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {choosing && (
        <div className="rc-scrim">
          <div className="rc kp-small" role="dialog" aria-modal="true" aria-labelledby="cv-start">
            <h2 id="cv-start">What would you like to see in your view?</h2>
            <p className="kp-faint">
              Describing a view for AgencyAI to build arrives with AgencyAI.
            </p>
            <div className="cf-choices">
              <button
                type="button"
                className="cf-choice"
                onClick={() => {
                  setChoosing(false);
                  setEditing({ view: null, name: '', definition: BLANK_VIEW });
                }}
              >
                <strong>Start a blank view</strong>
                <small>Choose the sources, dimension and columns yourself.</small>
              </button>
              {(views ?? []).map((view) => (
                <button
                  key={view.id}
                  type="button"
                  className="cf-choice"
                  onClick={() => {
                    setChoosing(false);
                    setEditing({
                      view: null,
                      name: `${view.name} (copy)`,
                      definition: view.definition,
                    });
                  }}
                >
                  <strong>Clone {view.name}</strong>
                  <small>Start from this view.</small>
                </button>
              ))}
            </div>
            <div className="kp-dialog-actions">
              <button type="button" className="btn secondary" onClick={() => setChoosing(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {editing && (
        <ViewEditor
          organisationId={organisationId}
          clientId={client.id}
          timezone={client.timezone ?? DEFAULT_TIMEZONE}
          sources={sources}
          view={editing.view}
          start={{ name: editing.name, definition: editing.definition }}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      )}
      {deleting && (
        <ConfirmDelete
          name={deleting.name}
          note="Dashboard widgets that show it will say the view is gone."
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const target = deleting;
            setDeleting(null);
            void customApi.removeView(organisationId, target.id).then(changed);
          }}
        />
      )}
    </div>
  );
}
